import { HttpError, api } from '@/api/client'
import { PageHeader } from '@/components/shared/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { hasPermission } from '@/hooks/usePermission'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/stores/auth.store'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, redirect } from '@tanstack/react-router'
import { CheckCircle2, Info, Paperclip } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsReadOnly } from '@/hooks/useSubscription'
import { Route as settingsLayoutRoute } from './layout'

type Provider = 'log' | 'resend'

// Form state kept local to the component — we only POST the fields the
// user actually changed so an accidental resend_key echo is avoided.
interface FormState {
  provider: Provider
  from_name: string
  from_email: string
  // Empty string = "don't touch the stored key" per backend contract.
  // "-" is a sentinel the user never types directly; we drive the
  // "clear key" path via a distinct Clear button.
  resend_key: string
}

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())
}

function EmailSettingsPage() {
  const { t } = useTranslation('settings')
  const toast = useToast()
  const readOnly = useIsReadOnly()
  const qc = useQueryClient()

  const { data: org, isPending } = useQuery({
    queryKey: ['org'],
    queryFn: api.org.get,
  })

  const config = org?.mailer_config
  const [form, setForm] = useState<FormState>({
    provider: 'log',
    from_name: '',
    from_email: '',
    resend_key: '',
  })

  useEffect(() => {
    if (config) {
      setForm({
        provider: config.provider as Provider,
        from_name: config.from_name,
        from_email: config.from_email,
        resend_key: '',
      })
    }
  }, [config])

  const save = useMutation({
    mutationFn: (body: Parameters<typeof api.org.updateMailerConfig>[0]) =>
      api.org.updateMailerConfig(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['org'] })
      toast.show({ variant: 'success', title: t('email.saved') })
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: t('email.save_failed'),
        description: err instanceof HttpError ? err.message : undefined,
      })
    },
  })

  const clearKey = useMutation({
    mutationFn: () => api.org.updateMailerConfig({ resend_key: '-' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['org'] })
      setForm((f) => ({ ...f, resend_key: '' }))
      toast.show({ variant: 'success', title: t('email.key_cleared') })
    },
  })

  function handleSave() {
    const body: Parameters<typeof api.org.updateMailerConfig>[0] = {
      provider: form.provider,
      from_name: form.from_name.trim(),
      from_email: form.from_email.trim(),
    }
    // Only send resend_key when the user actually typed one — otherwise
    // the server leaves the stored value alone.
    const k = form.resend_key.trim()
    if (k.length > 0) body.resend_key = k
    save.mutate(body)
  }

  if (isPending || !config) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('email.title')} description={t('email.description')} />
        <Card>
          <CardContent className="flex flex-col gap-4 p-5">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  const canSave =
    form.from_name.trim().length > 0 &&
    isValidEmail(form.from_email) &&
    (form.provider === 'log' ||
      (form.provider === 'resend' && (config.has_resend_key || form.resend_key.trim().length > 0)))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('email.title')} description={t('email.description')} />

      <Card>
        <CardContent className="flex flex-col gap-6 p-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">{t('email.identity.title')}</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {t('email.identity.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email-from-name">
                {t('email.from_name')} <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="email-from-name"
                value={form.from_name}
                placeholder="AMA Transport"
                maxLength={120}
                onChange={(e) => setForm((f) => ({ ...f, from_name: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email-from-email">
                {t('email.from_email')} <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="email-from-email"
                type="email"
                value={form.from_email}
                placeholder="contact@ama-transport.sn"
                maxLength={254}
                onChange={(e) => setForm((f) => ({ ...f, from_email: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t('email.provider.title')}</Label>

            <ProviderOption
              id="log"
              current={form.provider}
              onSelect={(p) => setForm((f) => ({ ...f, provider: p }))}
              title={t('email.provider.log.title')}
              description={t('email.provider.log.description')}
              badge={t('email.provider.log.badge')}
            >
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                <span>{t('email.provider.log.hint')}</span>
              </div>
            </ProviderOption>

            <ProviderOption
              id="resend"
              current={form.provider}
              onSelect={(p) => setForm((f) => ({ ...f, provider: p }))}
              title={t('email.provider.resend.title')}
              description={t('email.provider.resend.description')}
              badge={t('email.provider.resend.badge')}
            >
              {form.provider === 'resend' ? (
                <div className="mt-3 flex flex-col gap-1.5">
                  <Label htmlFor="email-resend-key" className="text-xs text-muted-foreground">
                    {t('email.resend.key_label')}
                  </Label>
                  <Input
                    id="email-resend-key"
                    type="password"
                    placeholder={
                      config.has_resend_key ? t('email.resend.key_set') : 're_xxxxxxxxxxxxx'
                    }
                    value={form.resend_key}
                    onChange={(e) => setForm((f) => ({ ...f, resend_key: e.target.value }))}
                    className="font-mono text-sm"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      {config.has_resend_key
                        ? t('email.resend.key_hint_present')
                        : t('email.resend.key_hint_absent')}
                    </p>
                    {config.has_resend_key ? (
                      <button
                        type="button"
                        onClick={() => clearKey.mutate()}
                        disabled={clearKey.isPending}
                        className="text-xs text-muted-foreground underline transition-colors hover:text-rose-600 dark:hover:text-rose-400"
                      >
                        {t('email.resend.clear_key')}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </ProviderOption>
          </div>

          <div className="flex justify-end border-t border-border pt-4">
            <Button
              onClick={handleSave}
              loading={save.isPending}
              loadingLabel={t('email.saving')}
              disabled={readOnly || !canSave}
            >
              {t('email.save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <EmailPreviewCard config={form} orgName={org.name} />
    </div>
  )
}

interface ProviderOptionProps {
  id: Provider
  current: Provider
  onSelect: (id: Provider) => void
  title: string
  description: string
  badge: string
  children?: React.ReactNode
}

function ProviderOption({
  id,
  current,
  onSelect,
  title,
  description,
  badge,
  children,
}: ProviderOptionProps) {
  const selected = current === id
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      className={cn(
        'w-full rounded-xl border-2 p-4 text-left transition-all',
        selected ? 'border-primary' : 'border-border hover:border-border',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 transition-colors',
            selected ? 'border-primary bg-primary' : 'border-border',
          )}
          aria-hidden="true"
        />
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground">{title}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {badge}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
          {children}
        </div>
      </div>
    </button>
  )
}

function EmailPreviewCard({ config, orgName }: { config: FormState; orgName: string }) {
  const { t } = useTranslation('settings')
  const fromName = config.from_name.trim() || orgName
  const fromEmail = config.from_email.trim()

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div>
          <h3 className="text-base font-semibold text-foreground">{t('email.preview.title')}</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('email.preview.description')}</p>
        </div>

        <div className="overflow-hidden rounded-xl border border-border">
          <div className="space-y-1 border-b border-border bg-muted px-4 py-3 text-sm">
            <HeaderRow label={t('email.preview.from')}>
              <span className="font-medium">
                {fromName}{' '}
                <span className="font-normal text-muted-foreground">
                  &lt;{fromEmail || t('email.preview.from_email_missing')}&gt;
                </span>
              </span>
            </HeaderRow>
            <HeaderRow label={t('email.preview.to')}>client@exemple.sn</HeaderRow>
            <HeaderRow label={t('email.preview.subject')}>
              {t('email.preview.sample_subject', { orgName })}
            </HeaderRow>
          </div>

          <div className="space-y-3 bg-background p-4 text-sm">
            <p>{t('email.preview.sample_greeting')}</p>
            <p>{t('email.preview.sample_body_1')}</p>
            <p>{t('email.preview.sample_body_2')}</p>
            <span className="inline-block rounded-lg bg-primary px-4 py-2 text-xs font-medium text-primary-foreground">
              {t('email.preview.sample_cta')}
            </span>
          </div>

          <div className="flex items-center gap-2 border-t border-border bg-muted px-4 py-2">
            <Paperclip className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
            <span className="text-xs text-muted-foreground">DEVIS-QT-2026-04-00042.pdf</span>
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted px-3 py-2.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span>
            {config.provider === 'resend'
              ? t('email.preview.note_resend')
              : t('email.preview.note_log')}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

function HeaderRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-foreground">{children}</span>
    </div>
  )
}

export const Route = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/email',
  beforeLoad: () => {
    const perms = useAuthStore.getState().permissions
    if (!hasPermission(perms, 'org:update')) {
      throw redirect({ to: '/settings/profile' })
    }
  },
  component: EmailSettingsPage,
})
